import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { AppSettings, ITag, TagService } from 'pac-api';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, share, switchMap, tap } from 'rxjs/operators';

import { CatchError, empty, serializeQueryParam } from '../helpers';
import { LoggerService } from '../logger.service';
import { AddressModel } from '../model/address.model';
import { Association } from '../model/assoication';
import { Individual } from '../model/individual';
import { IndividualAddressModel } from '../model/individual-address.model';
import { MembershipModel } from '../model/membership.model';
import { PackageModel } from '../model/package.model';
import { ReferralModel } from '../model/referral-model';
import { SingleSignOnToken } from '../model/single-signon-token';
import { GenericErrorHandlerService } from '../shared/generic-error-handler.service';
import { CachedSet } from './../model/CachedSet';

@Injectable({
  providedIn: 'root'
})
export class WizardService {

  static PastDate(control: UntypedFormControl): { [key: string]: any } {
    if (control.value) {
      const now = new Date(); now.setHours(0, 0, 0, 0);
      const date = new Date(control.value); date.setHours(0, 0, 0, 0);

      if (date > now) {
        return { 'PastDate': true };
      }
    }

    return null;
  }

  static FutureDate(control: UntypedFormControl): { [key: string]: any } {
    if (control.value) {
      const now = new Date(); now.setHours(0, 0, 0, 0);
      const date = new Date(control.value); date.setHours(0, 0, 0, 0);

      if (date < now) {
        return { 'FutureDate': true };
      }
    }

    return null;
  }

  static PlausibleDate(control: UntypedFormControl): { [key: string]: any } {
    if (control.value) {
      const min = new Date(1930, 1, 1, 0, 0, 0, 0);
      const max = new Date(2030, 12, 31, 0, 0, 0, 0);
      const date = new Date(control.value);

      if (!Number.isNaN(date.valueOf()) && (date < min || date > max)) {
        return { 'PlausibleDate': true };
      }
    }

    return null;
  }

  static ValidDate(control: UntypedFormControl): { [key: string]: any } {
    if (control.value) {
      const date = new Date(control.value);

      if (date instanceof Date) {
        if (isNaN(date.valueOf()) === false) {
          return null;
        }
      }

      return { 'ValidDate': true };
    }

    return null;
  }

  public IsStudentPortal: boolean = false;

  public Address: Observable<IndividualAddressModel> = null;

  public set TermsAgreement(terms: Date) {
    sessionStorage.setItem('Wizard-TermsAgreement-' + this.Package.p02_key, terms.toString());
  }

  public get TermsAgreement(): Date {
    if (this.Package) {
      const terms = sessionStorage.getItem('Wizard-TermsAgreement-' + this.Package.p02_key);
      if (terms) {
        return new Date(terms);
      }
    }
  }

  public SetAdditionalAcknowledgement(ackTypeKey: string) {
    sessionStorage.setItem('Wizard-AddlAcknowledgement-' + ackTypeKey, (new Date).toString());
  }

  public GetAdditionalAcknowledgements(): {key: string, agreed: Date}[] {
    let acks = [];
    for(let a=0; a<sessionStorage.length; a++) {
      const key = sessionStorage.key(a);
      if (key.startsWith('Wizard-AddlAcknowledgement-')) {
        const value = sessionStorage.getItem(key);
        const k = key.substring(27);
        acks.push({key: k, agreed: new Date(value)});
      }
    }
    return acks;
  }

  public Checklist: CachedSet<string>;

  public get Individual(): Individual {
    return this.individual;
  }

  private individual: Individual;

  private address: IndividualAddressModel;

  private metadata: Map<string, any>;

  private packages: Map<string, PackageModel>;

  private referral: ReferralModel;

  private waitOnTasks: Set<Observable<any>>;

  constructor(
    router: Router,
    @Inject(PLATFORM_ID) platformId: any,
    private http: HttpClient,
    private logger: LoggerService,
    private settings: AppSettings,
    private genericErrorHandler: GenericErrorHandlerService,
    private tagService: TagService
  ) {
    this.metadata = new Map();
    this.packages = new Map();
    this.waitOnTasks = new Set();
    this.Checklist = new CachedSet('Wizard.Checklist');

    this.individual = new Individual();

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key.startsWith('Wizard-Meta-')) {
        const value = sessionStorage.getItem(key);
        const k = key.substring(12);
        this.metadata.set(k, JSON.parse(value));
      }
    }

    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.waitOnTasks.clear();
      }
    });

    document.domain = this.settings.document_domain;

    try {
      logger.log('WizardService constructor');
    } catch {
    }
  }

  getIndividual(): Individual {
    return this.individual;
  }

  setIndiviudal(individual: any) {
    Object.assign(this.individual, individual);
  }

  getAddress(): IndividualAddressModel {
    return this.address;
  }

  setAddress(address: IndividualAddressModel | AddressModel): IndividualAddressModel {
    if (this.address === null || this.address === undefined) {
      this.address = new IndividualAddressModel();
    }

    if (typeof address === 'object') {
      this.address = Object.assign(this.address, address);
    }

    return this.address;
  }

  public Ping(): Observable<any> {
    return this.http.get(
      this.settings.pac_base_nf_url + 'api/Session',
      {
        withCredentials: true
      }
    );
  }

  public OptInSMS(optinSource: string): Observable<any> {
    let data = { cst_sms_opt: 'Opt In', cst_cph_sms_key: null, cst_sms_consent_type: optinSource };
    return this.http.post(
      this.settings.pac_base_nf_url + 'api/SMS', data,
      {
        withCredentials: true
      });
  }

  public OptInGradGift(optIn: boolean): Observable<ITag> {
    optIn = optIn ?? false;
    return this.tagService.Insert({
                  Value: optIn,
                  p32_key: '4222ac65-29b2-49e6-86ea-04da211ee8e6'
                });
  }

  public CreateAccount(gRecaptchaResponse: string): Observable<any> {
    if (gRecaptchaResponse) {
      this.individual.hit_student_portal = this.IsStudentPortal;
      const observable = this.http.post(
        this.settings.pac_base_nf_url + 'api/Individual',
        this.individual,
        {
          params: {
            'Site': this.settings.pac_base_asn_code,
            'gRecaptchResponse': gRecaptchaResponse
          },
          withCredentials: true
        }
      );

      return observable.pipe(catchError(CatchError)).pipe(map((result: any) => {
        if (result.error) {
          return result;
        } else if (result.cst_key) {
          const individual = new Individual(result);
          this.setIndiviudal(individual);
          return individual;
        }
      }), tap(result => {
        try {
          this.logger.info(result);

          if (result instanceof Individual && typeof ((window as any).H) === 'object') {
            (window as any).H.identify(result.cst_recno.toString(), {
              name: result.ind_full_name_cp,
              email: result.eml_address,
              cst_member_flag: result.cst_member_flag ? 'true' : 'false'
            });
          }
        } catch {
        }
      }), switchMap(result => {
        if (result.cst_key) {
          return this.TokenLogin();
        } else {
          return of(result);
        }
      }));
    }
  }

  public UpdateAccount(): Observable<Individual> {
    this.individual.hit_student_portal = this.IsStudentPortal;
    const observable = this.http.put(
      this.settings.pac_base_nf_url + 'api/Individual',
      this.individual,
      {
        withCredentials: true
      }
    );

    const shared = observable.pipe(catchError(CatchError)).pipe(map((result: any) => {
      if (result) {
        if (result.error) {
          return result;
        } else if (result.cst_key) {
          const individual = new Individual(result);
          this.setIndiviudal(individual);
          return individual;
        }
      }
    }), share());

    shared.subscribe(result => {
      try {
        this.logger.info(result);

        if (result instanceof Individual && typeof ((window as any).H) === 'object') {
          (window as any).H.identify(result.cst_recno.toString(), {
            name: result.ind_full_name_cp,
            email: result.eml_address,
            cst_member_flag: result.cst_member_flag ? 'true' : 'false'
          });
        }
      } catch { }
    }, this.genericErrorHandler.Hanlder);

    return shared;
  }

  public GetMemberships(checkStudentPortalLocked: boolean = false): Observable<MembershipModel[]> {
    return this.http.get(
      this.settings.pac_base_nf_url + 'api/Membership' + (checkStudentPortalLocked ? '?checkStudentPortalLocked=true' : ''),
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map(result => {
      if (result) {
        if (result.error) {
          return result;
        } else if (result instanceof Array) {
          return result.map(mbr => new MembershipModel(mbr));
        }
      }
    }), tap(result => {
      try {
        this.logger.info(result);
      } catch {
      }
    }, this.genericErrorHandler.Hanlder));
  }

  public TokenLogin(asn: Association = this.settings.pac_base_asn_code, token?: SingleSignOnToken): Observable<Individual> {
    const shared = new Subject<Individual>();

    if (asn === this.settings.pac_base_asn_code && this.individual.ind_token) {
      const data = new FormData();
      data.append('Token', this.individual.ind_token);

      const request = this.http.post(`/pac-wizard/login/${this.individual.eml_address}`, data);
      request.subscribe(shared);

      shared.subscribe((result: any) => {
        try {
          this.logger.info(result);
        } catch { }

        if (result.individual && result.individual.cst_key) {
          const individual = new Individual(result.individual);
          this.setIndiviudal(individual);

          try {
            if (result instanceof Individual && typeof ((window as any).H) === 'object') {
              (window as any).H.identify(result.cst_recno.toString(), {
                name: result.ind_full_name_cp,
                email: result.eml_address,
                cst_member_flag: result.cst_member_flag ? 'true' : 'false'
              });
            }
          } catch {
          }
        }
      });

      return shared;
    } else if (asn && token) {
      const url = this.settings.systemoptions['PAC.Web.' + asn];
      const data = new FormData();
      data.append('Token', token.Token);

      const request = this.http.post(url + `pac-wizard/login/${this.individual.eml_address}`, data, { withCredentials: true });
      request.subscribe(shared);

      shared.subscribe((result: any) => {
        try {
          this.logger.info(result);
        } catch { }
      });

      return shared;
    }
  }

  public SyncIndividual(): Observable<Individual> {
    if (this.individual.cst_key) {
      const request = this.http.get(`/pac-wizard/login/${this.individual.eml_address}`, { withCredentials: true }).pipe(
        catchError(CatchError)).pipe(map(result => {
          if (result && 'individual' in result) {
            if (result.individual && result.individual.cst_key) {
              return new Individual(result.individual);
            }
          }
        }), tap(result => {
          if (result && result instanceof Individual) {
            this.setIndiviudal(result);

            try {
              if (result instanceof Individual && typeof ((window as any).H) === 'object') {
                (window as any).H.identify(result.cst_recno.toString(), {
                  name: result.ind_full_name_cp,
                  email: result.eml_address,
                  cst_member_flag: result.cst_member_flag ? 'true' : 'false'
                });
              }
            } catch {
            }
          }
        }, this.genericErrorHandler.Hanlder));

      return request;
    }
  }

  public GetSSOToken(): Observable<SingleSignOnToken> {
    const observable = this.http.get(
      this.settings.pac_base_nf_url + 'api/Helper/GenerateSingleSignOnToken',
      {
        withCredentials: true
      }
    );

    const shared = observable.pipe(catchError(CatchError)).pipe(map((result: any) => {
      if (result.error) {
        return result;
      } else if (result.Token) {
        return new SingleSignOnToken(result);
      }
    }), share());

    shared.subscribe(result => {
      try {
        this.logger.info(result);
      } catch { }
    }, this.genericErrorHandler.Hanlder);

    return shared;
  }

  public GetPackage(key: string): Observable<PackageModel> {
    if (this.packages.has(key)) {
      return of(this.packages.get('key'));
    }

    const observable = this.http.get(
      this.settings.pac_base_nf_url + 'api/AffinityPortal/Packages/' + key,
      {
        withCredentials: true
      }
    );

    const shared = observable.pipe(catchError(CatchError)).pipe(map((result: any) => {
      if (result.error) {
        return result;
      } else if (result.p02_combo_description) {
        return new PackageModel(result);
      }
    }), share());

    shared.subscribe(result => {
      try {
        this.packages.set(key, result);
        this.logger.info(result);
      } catch { }
    }, this.genericErrorHandler.Hanlder);

    return shared;
  }

  public GetReferral(cst_recno: number): Observable<ReferralModel> {
    if (this.referral) {
      if (this.referral.cst_recno === cst_recno) {
        return of(this.referral);
      }
    }

    const observable = this.http.get(
      this.settings.pac_base_nf_url + 'api/Helper/Referral/' + cst_recno,
      {
        withCredentials: true
      }
    );

    const shared = observable.pipe(catchError(CatchError)).pipe(map((result: any) => {
      if (result.error) {
        return result;
      } else if (result.cst_recno) {
        return new ReferralModel(result);
      }
    }), share());

    shared.subscribe(result => {
      try {
        this.referral = result;
        this.logger.info(result);
      } catch { }
    }, this.genericErrorHandler.Hanlder);

    return shared;
  }

  public waitOn<T>(task: Observable<T>): Observable<T> {
    this.waitOnTasks.add(task);
    return task;
  }

  public wait(): Observable<any> {
    if (this.waitOnTasks.size) {
      const shared = new Subject<any>();
      forkJoin(...Array.from(this.waitOnTasks)).subscribe(shared);
      shared.subscribe(_ => {
        this.waitOnTasks.clear();
      });
      return shared;
    } else {
      return of(null);
    }
  }

  public GetExternalLink(asn: Association, uri: string, query?: any) {
    const search = (query) ? serializeQueryParam(query) : null;

    let url: string;

    if (this.settings.pac_base_asn_code === asn) {
      url = '/';
    } else {
      url = this.settings.systemoptions['PAC.Web.' + asn];
    }

    url += uri;

    if (search) {
      url += search;
    }

    return url;
  }

  public Clear(): void {
    sessionStorage.removeItem('Wizard-Meta-Affinity');
    sessionStorage.removeItem('Wizard-Meta-TBS');
    sessionStorage.removeItem('Wizard-Meta-Package');
    sessionStorage.removeItem('Wizard-Meta-Referral');
    // Note we don't want to clear Wizard.Checklist here
  }

  public set Affinity(affinity: number) {
    if (empty(affinity)) {
      if (this.metadata.has('Affinity')) {
        this.metadata.delete('Affinity');
      }
    } else {
      this.metadata.set('Affinity', affinity);
      sessionStorage.setItem('Wizard-Meta-Affinity', JSON.stringify(affinity));
    }
  }

  public get Affinity(): number {
    if (this.metadata.has('Affinity')) {
      return this.metadata.get('Affinity');
    }
  }

  public get isAffinity(): boolean {
    return this.metadata.has('Affinity');
  }

  public get isTBS(): boolean {
    if (this.metadata.has('TBS')) {
      return this.metadata.get('TBS');
    }
  }

  public set isTBS(value: boolean) {
    if (value) {
      this.metadata.set('TBS', true);
      sessionStorage.setItem('Wizard-Meta-TBS', JSON.stringify(true));
    } else {
      this.metadata.delete('TBS');
      sessionStorage.removeItem('Wizard-Meta-TBS');
    }
  }

  public get Package(): PackageModel {
    if (this.metadata.has('Package')) {
      return this.metadata.get('Package');
    }
  }

  public set Package(value: PackageModel) {
    if (value) {
      this.metadata.set('Package', value);
      sessionStorage.setItem('Wizard-Meta-Package', JSON.stringify(value));
    } else {
      this.metadata.delete('Package');
      sessionStorage.removeItem('Wizard-Meta-Package');
    }
  }

  public get Referral(): number {
    if (this.metadata.has('Referral')) {
      return this.metadata.get('Referral');
    }
  }

  public set Referral(recno: number) {
    if (recno) {
      const referral = this.metadata.has('Referral') ? this.metadata.get('Referral') : null;

      this.metadata.set('Referral', recno);
      sessionStorage.setItem('Wizard-Meta-Referral', JSON.stringify(recno));

      if (recno !== referral) {
        this.referral = null;
      }
    } else {
      this.metadata.delete('Referral');
      sessionStorage.removeItem('Wizard-Meta-Referral');
    }
  }

  public get HasReferral(): boolean {
    return this.metadata.has('Referral');
  }

  public setReferral(referral: ReferralModel): void {
    if (referral) {
      this.referral = referral;
    } else {
      this.Referral = null;
    }
  }

  public get HasDestination(): boolean {
    return this.metadata.has('Destination');
  }

  public get Destination(): URL {
    if (this.metadata.has('Destination')) {
      return this.metadata.get('Destination');
    }
  }

  public set Destination(destination: URL) {
    if (destination) {
      this.metadata.set('Destination', destination);
    } else {
      this.metadata.delete('Destination');
    }
  }

  public get HasProspectType(): boolean {
    return this.metadata.has('ProspectType');
  }

  public get ProspectType(): string {
    if (this.metadata.has('ProspectType')) {
      return this.metadata.get('ProspectType');
    }
  }

  public set ProspectType(prospectType: string) {
    if (prospectType) {
      this.metadata.set('ProspectType', prospectType);
    } else {
      this.metadata.delete('ProspectType');
    }
  }

  public get HasPromo(): boolean {
    return this.metadata.has('Promo');
  }

  public get Promo(): string {
    if (this.metadata.has('Promo')) {
      return this.metadata.get('Promo');
    }
  }

  public set Promo(promo: string) {
    if (promo) {
      this.metadata.set('Promo', promo);
    } else {
      this.metadata.delete('Promo');
    }
  }

}
